import * as z from 'zod';

import { zodDateOrString } from '../../utils';

/**
 * #### Fannie Mae Escrow Reporting: escrow event
 *
 * One row of the escrow-event upload CSV consumed by Fannie Mae's Servicing Platform
 * ("Upload Event(s)" flow). Each row is one escrow event carrying the running category
 * balance after that event and a per-loan sequence number. Fannie validates continuity
 * server-side: prior accepted balance + item amount must equal the reported balance.
 */
export const FannieMaeEscrowCategoryType = z.enum(['TaxAndInsurance', 'LossDraft', 'Holdback', 'InterestSubsidy']);
export type FannieMaeEscrowCategoryType = z.infer<typeof FannieMaeEscrowCategoryType>;

export const FannieMaeEscrowItemType = z.enum([
  'BuydownSubsidy',
  'CityPropertyTax',
  'CountyPropertyTax',
  'EarthquakeInsurance',
  'EscrowCloseout',
  'EscrowHoldback',
  'EscrowRefund',
  'FireInsurance',
  'FloodInsurance',
  'HailInsurance',
  'HomeownersInsurance',
  'InsuranceRefund',
  'InterestOnEscrow',
  'LenderPlacedInsurance',
  'LoanEscrowPayment',
  'Modification',
  'MortgageInsurance',
  'MortgageInsuranceRefund',
  'Other',
  'OtherInsurance',
  'OtherRefund',
  'OtherTax',
  'RestrictedEscrow',
  'SchoolPropertyTax',
  'Setup',
  'TaxRefund',
  'TownPropertyTax',
  'VolcanoInsurance',
  'WindstormInsurance',
]);
export type FannieMaeEscrowItemType = z.infer<typeof FannieMaeEscrowItemType>;

export const FannieMaeEscrowEventKind = z.enum(['setup', 'adjustment', 'activity']);
export type FannieMaeEscrowEventKind = z.infer<typeof FannieMaeEscrowEventKind>;

export const FannieMaeEscrowEventV1 = z.object({
  version: z.literal('1').describe('version'),
  /**
   * ##### Servicer Number
   * 9-digit numeric value from `company.thirdPartyIds.fnmaServicerId`.
   */
  servicerNumber: z.string().describe('string'),
  /**
   * ##### Fannie Mae Loan Number
   * 10-digit numeric value from `loan.configuration.investor.investorLoanId`.
   */
  fannieMaeLoanNumber: z.string().describe('string'),
  /**
   * ##### Loan Event Sequence Number
   * Integer >= 1, unique and increasing per loan across files: each file continues
   * from the loan's last persisted number (gaps allowed).
   */
  sequenceNumber: z.number().describe('number'),
  /**
   * ##### Loan Servicer Transaction Processed Date
   * `YYYY-MM-DD`; Fannie rejects future dates.
   */
  transactionProcessedDate: zodDateOrString.describe('date'),
  /**
   * ##### Loan Escrow Contractual Payment Amount
   * The loan's monthly T&I escrow payment, >= 0.00, reported on every event in every
   * category; 0.00 when the loan has no active T&I escrow account.
   */
  contractualPaymentAmount: z.number().describe('currency'),
  /**
   * ##### Loan Escrow Item Category Type
   */
  categoryType: FannieMaeEscrowCategoryType.describe('enum'),
  /**
   * ##### Loan Escrow Item Type
   * Valid values depend on the category (see the CategoryAndType pairing in the spec).
   */
  itemType: FannieMaeEscrowItemType.describe('enum'),
  /**
   * ##### Loan Escrow Item Amount
   * Non-zero, signed: the category balance delta this event produced.
   */
  itemAmount: z.number().describe('currency'),
  /**
   * ##### Loan Escrow Balance Amount
   * The category balance after this event. Fannie permits negatives only for
   * TaxAndInsurance.
   */
  balanceAmount: z.number().describe('currency'),
  /**
   * The `balanceHistory` event id this row was derived from. Absent on drift
   * adjustments (they re-anchor the chain rather than mirror one Willow event).
   * Bookkeeping only; not a CSV column.
   */
  sourceEventId: z.string().optional().describe('string'),
  /**
   * Bookkeeping only; not a CSV column.
   */
  eventKind: FannieMaeEscrowEventKind.describe('enum'),
});
export type FannieMaeEscrowEventV1 = z.infer<typeof FannieMaeEscrowEventV1>;

export const FannieMaeEscrowEvent = FannieMaeEscrowEventV1;
export type FannieMaeEscrowEvent = z.infer<typeof FannieMaeEscrowEvent>;
// Make `FannieMaeEscrowEvent` a discriminated union on 'version' when changes happen
